import { css, html, nothing } from "lit";
import { customElement, property, state } from 'lit/decorators.js';
import { connect } from "pwa-helpers";
import "./venmo-button";
import paymentStyles from "../../../common/styles/payment-styles"
import { PaymentMethod, PaymentMethodTemplate } from "../payment-method";
import store from "../../../store/store";
import { ClientTokenDetails, PaymentDetails, PaymentType } from "../../checkout/shop-checkout.interface";
import { StoreState } from "../../../store/store.interface";
import { getAddressValidationError, getAddressValidationStatus } from "../../address/store/address-validation";
import { ActionStatus } from "../../../common/constants/action-status";
import { handleApiError } from "../../../common/utils/api-utils";
import { getCart } from "../../cart/shop-cart/store/shop-cart.selectors";
import { getBillingAddress, getClientTokenResponse, getPaymentDetails, isShippingAddressFromAddressbook, updateBillingAddress, updateEditBillingAddressEditMode, resetPaymentDetails } from "../../checkout/shop-checkout";
import { isSegUser, selectUser } from "../../user-profile/user-profile.selectors";
import { waitForElementsToLoad } from "../../../common/utils/delay-util";
import { renderVenmoDisplay } from "../../../common/utils/payment-util";


/**
 * Venmo payment method that loads Venmo button. 
 * Renders Venmo payment results.
 */
 @customElement(`mhecomm-venmo-payment-method`)
 export class VenmoPaymentMethod extends connect(store)(PaymentMethod) implements PaymentMethodTemplate {

  static styles = [paymentStyles, css`
  #venmobutton {
    width: 285px;
  }
`];

  /**
   * Currency for Venmo transaction.
   */
  @property()
  currency = 'USD';

  /**
   * Braintree Client token to initialize Venmo button.
   */
  @state()
  private clientToken = "";

  @state()
  private venmoProfileId = "";

  @state()
  private venmoPaymentDetails : PaymentDetails | undefined;

  
  stateChanged(state: StoreState): void {
    this.updateIsSelected(PaymentType.BRAINTREE_VENMO, state);

    if (getAddressValidationStatus(state) === ActionStatus.failed) {
      handleApiError(getAddressValidationError(state));
      return;
    }

    this.venmoPaymentDetails = getPaymentDetails(state);
    const cart = getCart(state);
    this.shippable = cart?.shippable!;
  }

  connectedCallback(): void {
    super.connectedCallback();
     const clientTokenResponse: ClientTokenDetails | undefined = getClientTokenResponse(store.getState() as StoreState)
     this.clientToken = clientTokenResponse!.token;
     this.venmoProfileId = clientTokenResponse!.venmoProfileId;
     this.showUseShippingAddressButton = isSegUser(selectUser(store.getState() as StoreState)) ? !isShippingAddressFromAddressbook(store.getState() as StoreState) : true;
  
  }

  paymentMethodEditAction(): void {
    store.dispatch(updateEditBillingAddressEditMode(true));
    waitForElementsToLoad(this.shadowRoot!, ["mhecomm-address-form"]).then(() => {
      this.getAddressForm().prepopulate(getBillingAddress(store.getState() as StoreState)!);
      this.billingAddress = getBillingAddress(store.getState() as StoreState)!;
    });
  }

  public async validatePaymentMethod(): Promise<void> {
    if(!this.venmoPaymentDetails?.paymentNonce) {
      throw new Error("Venmo authorization failed.");
    }
    if(!this.isSameAsShippingAddress ){
      const addressForm = this.getAddressForm();
      await addressForm.validate()
      .then(addressValidationInfo => {
        if(addressValidationInfo){
          store.dispatch(updateBillingAddress(addressValidationInfo));
        }
      })
    }
  }

  renderComp() {
    if (!this.isSelected) {
      return nothing;
    }

    return html`
     ${this.venmoPaymentDetails?.venmoUserName ? this.renderBillingAddress() : this.renderVenmoButton()}
    `;
  }

  renderBillingAddress() {
    return html `
    <div class="d-flex flex-row m-1">
            ${renderVenmoDisplay(this.venmoPaymentDetails?.venmoUserName!)}
      <div class="mb-4 ms-2">
        <button class="btn btn-link" id="venmo-remove" aria-label="Remove Venmo payment method" autofocus
          @click=${this._onClickRemove}>
          Remove</button>
      </div>
    </div>
    ${this.venmoRemoveButtonFocus()}
    ${this.renderBillingAddressForm()}
    `
  }

  public venmoRemoveButtonFocus() {
    setTimeout(() => {
      if (this.venmoPaymentDetails?.venmoUserName) {
        (this.shadowRoot!.querySelector('#venmo-remove') as HTMLElement)?.focus();
    }
    }, 500);
  }

  renderVenmoButton() {
    return html`
    <p class="fs-6 fw-bold">To pay with Venmo:</p>
      <div class="justify-content-start">
        <div id="venmobutton"></div>
          <mhecomm-venmo-button 
            .amount = "${this.totalAmount}"
            .clientToken="${this.clientToken}"
            .profileId="${this.venmoProfileId}">
          </mhecomm-venmo-button>
          
        </div>
      </div>
          <p class="mt-3">
            1. Click the Venmo button and scan the QR code with your phone. <br>
            2. Review and confirm the payment details on your Venmo app.<br>
            3. Come back to this page to complete your transaction.</p>
    `;
  }

  _onClickRemove() {
    this.venmoPaymentDetails = undefined;
    store.dispatch(resetPaymentDetails());
    store.dispatch(updateBillingAddress(undefined));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-venmo-payment-method': VenmoPaymentMethod;
  }
}
